import type { AnalysisConfig } from '@parlay-picker/shared/analysisConfig';
import type { ConfigPreset } from '@parlay-picker/shared/configPreset';
import type { DisputeStatus } from '@parlay-picker/shared/disputeStatus';
import type { LeagueKey } from '@parlay-picker/shared/league';
import type { NotificationPreferences } from '@parlay-picker/shared/notification';
import type {
  EuGameMarketSportsbookId,
  OddsApiSportsbookId,
  SportsbookId,
} from '@parlay-picker/shared/sportsbook';
import type { SubscriptionTier } from '@parlay-picker/shared/subscriptionTier';
import { DEFAULT_THEME_ID } from '@parlay-picker/shared/themes';
import type { ReactNode } from 'react';

// =============================================================================
// Types
// =============================================================================

/**
 * User preferences with required defaults.
 * All fields are non-nullable and have DB-level defaults.
 */
export interface UserPreferences {
  /** Minimum bets required to show players/props in performance analysis. Default: 3 */
  minSampleSizeForInsights: number;
  /** User's preferred sportsbook for odds/lines. Default: 'fanduel' */
  preferredSportsbook: SportsbookId;
  /** The book used for EuroLeague. Unset: Pinnacle */
  preferredEuSportsbook?: EuGameMarketSportsbookId;
  /** Auto-apply picks when generation completes (skip confirmation). Default: false */
  autoApplyPicks?: boolean;
  /** User's preferred visual theme. Default: 'dark-electric' */
  theme?: string;
  /** Show best line badges and odds comparison (Pro+ only). Default: true */
  enableLineComparison?: boolean;
  /** Show coin flip animation when quota is consumed. Default: true */
  showQuotaNotifications?: boolean;
  /** Enable premium tier grouping animation when picks complete. Default: true */
  enablePremiumAnimations?: boolean;
  /** Per-sub-group notification channel preferences (in-app, email, web push) */
  notifications?: NotificationPreferences;
  /** Show contextual betting tips for new users. Default: true (auto-disabled after all seen) */
  showBettingTips?: boolean;
  /** User's preferred league for pick generation. Default: 'NBA' */
  preferredLeague?: string;
  /** Books whose line moves send slate alerts. Unset: the preferred sportsbook. Empty: none */
  slateAlertBooks?: OddsApiSportsbookId[];
}

/** Default preferences used when user data is missing preferences */
export const DEFAULT_PREFERENCES: UserPreferences = {
  minSampleSizeForInsights: 3,
  preferredSportsbook: 'fanduel',
  autoApplyPicks: false,
  theme: DEFAULT_THEME_ID,
};

/**
 * Ensure user has valid preferences with defaults applied.
 * Handles legacy users who may have empty/null preferences.
 */
export function normalizeUser(user: User): User {
  return {
    ...user,
    preferences: {
      ...DEFAULT_PREFERENCES,
      ...user.preferences,
    },
  };
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  role: SubscriptionTier;
  emailVerified: boolean;
  pendingEmail?: string | null;
  preferences: UserPreferences;
  analysisConfig?: AnalysisConfig;
  bannedPlayers?: string[];
  configPresets?: ConfigPreset[];
  createdAt?: string;
  // Legal document acceptance tracking
  tosAcceptedAt?: string | null;
  tosVersion?: string | null;
  privacyPolicyAcceptedAt?: string | null;
  privacyPolicyVersion?: string | null;
  needsLegalReAcceptance?: boolean;
  // Onboarding
  onboardingCompletedAt?: string | null;
  // OAuth
  hasPassword?: boolean;
  // Payment dispute status
  disputeStatus?: DisputeStatus | null;
}

export interface RegisterOptions {
  firstName: string;
  lastName: string;
  displayName: string;
  tosAccepted: boolean;
  inviteCode?: string;
}

export interface AuthResponse {
  user?: User;
  error?: string;
  success?: boolean;
  devToken?: string; // Dev-only: password reset token for testing
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  logoutReason: 'paused' | 'deleted' | null;
  clearLogoutReason: () => void;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<User>;
  logout: () => Promise<void>;
  register: (
    email: string,
    password: string,
    options: RegisterOptions,
  ) => Promise<User | { type: 'verification_required'; email: string }>;
  resendVerificationPublic: (email: string) => Promise<AuthResponse>;
  requestPasswordReset: (email: string) => Promise<AuthResponse>;
  resetPassword: (token: string, newPassword: string) => Promise<AuthResponse>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  updateBannedPlayers: (bannedPlayers: string[], league: LeagueKey) => Promise<void>;
  updateProfile: (profile: {
    displayName?: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResponse>;
  verifyEmail: (token: string) => Promise<AuthResponse>;
  resendVerificationEmail: () => Promise<AuthResponse>;
  requestEmailChange: (newEmail: string, password: string) => Promise<AuthResponse>;
  cancelEmailChange: () => Promise<void>;
  acceptLegalDocuments: () => Promise<AuthResponse>;
  completeOnboarding: () => Promise<void>;
  refetchUser: () => Promise<void>;
  needsOnboarding: boolean;
  authFetch: (url: string, options?: RequestInit) => Promise<Response>;
  deleteAccount: (password?: string) => Promise<void>;
  pauseAccount: (password?: string) => Promise<void>;
  oauthLogin: (
    provider: string,
    idToken: string,
    extra?: {
      displayName?: string;
      tosAccepted?: boolean;
      firstName?: string;
      lastName?: string;
      linkPassword?: string;
      inviteCode?: string;
    },
  ) => Promise<{
    type: string;
    user?: User;
    email?: string;
    provider?: string;
    firstName?: string;
    lastName?: string;
    deletedAt?: string;
    permanentDeletionDate?: string;
  }>;
}

export interface AuthProviderProps {
  children: ReactNode;
}
